import type { BadgeProps } from '@/components/ui/badge';
import type { FinanceExpected, FinanceRefund, FinanceRefundAttempt } from '@/api/generated/types.gen';

// Single source of truth: ops status enum → DS Badge variant + display label.
// Replaces the stock green/yellow/red/zinc colorMaps that were duplicated across
// bookings.index/$id, guests.$id and automation-scheduled.
export type BadgeVariant = NonNullable<BadgeProps['variant']>;

interface Tone {
  variant: BadgeVariant;
  label: string;
}

// Keys mirror the generated API unions exactly (types.gen.ts), so a generated
// `BookingDetail['status']` etc. indexes these maps without a cast.
export type BookingStatus =
  | 'pending'
  | 'confirmed'
  | 'cancelled'
  | 'no_show'
  | 'checked_in'
  | 'checked_out';
export type OperationalStatus =
  | 'upcoming'
  | 'in_house'
  | 'past'
  | 'pending'
  | 'cancelled'
  | 'no_show';
export type RevisionStatus = 'new' | 'modified' | 'cancelled';
export type ScheduledStatus = 'pending' | 'sent' | 'failed' | 'cancelled';
// Finance (ADR-043). Keys mirror the generated unions: FinancePeriod['status']
// and FinanceMovement['origin'].
export type FinancePeriodStatus = 'open' | 'closed';
export type FinanceMovementOrigin = 'operator' | 'import' | 'system';
// Slice 2 (spec §5.7). Derived via indexed access instead of retyped as a
// literal union like its siblings above: FinanceExpected comes from the
// generated SDK, so a status the server adds or removes shows up here as a
// compile error (an unhandled key in the Record below) instead of silent
// drift between this map and the wire.
export type FinanceExpectedStatus = FinanceExpected['status'];

export const BOOKING_STATUS_TONE: Record<BookingStatus, Tone> = {
  confirmed: { variant: 'success', label: 'Confirmed' },
  checked_in: { variant: 'success', label: 'Checked-in' },
  pending: { variant: 'warning', label: 'Pending' },
  cancelled: { variant: 'danger', label: 'Cancelled' },
  no_show: { variant: 'danger', label: 'No-show' },
  checked_out: { variant: 'neutral', label: 'Checked-out' },
};

// Derived operational status (server-computed, cross-map X1): the at-a-glance
// "where is this stay" badge shown on the bookings list + detail + calendar.
export const OPERATIONAL_STATUS_TONE: Record<OperationalStatus, Tone> = {
  upcoming: { variant: 'info', label: 'Upcoming' },
  in_house: { variant: 'success', label: 'In-house' },
  past: { variant: 'neutral', label: 'Past' },
  pending: { variant: 'warning', label: 'Pending' },
  cancelled: { variant: 'danger', label: 'Cancelled' },
  no_show: { variant: 'danger', label: 'No-show' },
};

export const REVISION_STATUS_TONE: Record<RevisionStatus, Tone> = {
  new: { variant: 'info', label: 'New' },
  modified: { variant: 'warning', label: 'Modified' },
  cancelled: { variant: 'danger', label: 'Cancelled' },
};

export const SCHEDULED_STATUS_TONE: Record<ScheduledStatus, Tone> = {
  pending: { variant: 'warning', label: 'Pending' },
  sent: { variant: 'success', label: 'Sent' },
  failed: { variant: 'danger', label: 'Failed' },
  cancelled: { variant: 'neutral', label: 'Cancelled' },
};

// Payment-schedule entry status (ADR-033). overdue is rendered as a separate
// pill, not a status value (it is a derived flag on an otherwise 'expected' row).
export type PaymentScheduleStatus = 'expected' | 'received' | 'waived' | 'cancelled';
export const PAYMENT_STATUS_TONE: Record<PaymentScheduleStatus, Tone> = {
  received: { variant: 'success', label: 'Received' },
  expected: { variant: 'warning', label: 'Expected' },
  waived: { variant: 'neutral', label: 'Waived' },
  cancelled: { variant: 'neutral', label: 'Cancelled' },
};

export type PaymentMethod = 'capture' | 'payout' | 'vcc_charge' | 'manual' | 'refund';
export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  capture: 'Capture',
  payout: 'Payout',
  vcc_charge: 'VCC charge',
  manual: 'Manual',
  // refund rows carry a negative amount_minor (OH-124 §3.4) — added to the
  // method enum in slice 2 (task 8) so the operator editor can render one.
  refund: 'Refund',
};

// A month is open until it is closed; 'closed' is not a failure, so it is
// neutral rather than danger.
export const FINANCE_PERIOD_STATUS_TONE: Record<FinancePeriodStatus, Tone> = {
  open: { variant: 'success', label: 'Open' },
  closed: { variant: 'neutral', label: 'Closed' },
};

// Where a movement came from. 'import' is the pre-cutover history loaded from
// the owner's spreadsheet and 'system' is written by the engine, not by a
// person — both are informational, neither is an error.
export const FINANCE_MOVEMENT_ORIGIN_TONE: Record<FinanceMovementOrigin, Tone> = {
  operator: { variant: 'info', label: 'Operator' },
  import: { variant: 'neutral', label: 'Imported' },
  system: { variant: 'neutral', label: 'System' },
};

// An expected payment's lifecycle (spec §5.7). 'draft' is a phone submission
// its author hasn't sent yet — neutral, not a problem; 'planned' is the
// queue's own steady state waiting on the finance team, so it gets the same
// "needs attention" warning tone the payment-schedule 'expected' status uses
// above, not danger (it isn't overdue by definition — due-ness is a
// separate, date-derived signal the queue renders itself, spec §8.1).
export const FINANCE_EXPECTED_STATUS_TONE: Record<FinanceExpectedStatus, Tone> = {
  draft: { variant: 'neutral', label: 'Draft' },
  planned: { variant: 'warning', label: 'Awaiting' },
  confirmed: { variant: 'success', label: 'Confirmed' },
  skipped: { variant: 'neutral', label: 'Skipped' },
  cancelled: { variant: 'danger', label: 'Cancelled' },
};

// Slice 3 (task 23). Same indexed-access derivation as FinanceExpectedStatus
// above: a status the server adds or removes is a compile error here, not
// silent drift.
export type FinanceRefundStatus = FinanceRefund['status'];
export type FinanceRefundAttemptStatus = FinanceRefundAttempt['status'];

// A refund obligation's own lifecycle (spec §9, provider events screen).
// 'open' is still awaiting the provider's own confirmation, so it gets the
// same warning tone every other "needs attention, not yet a problem" status
// in this file uses; 'executed' is the good outcome, muted like every other
// terminal-and-fine status.
export const FINANCE_REFUND_STATUS_TONE: Record<FinanceRefundStatus, Tone> = {
  open: { variant: 'warning', label: 'Awaiting provider' },
  executed: { variant: 'info', label: 'Refunded by provider' },
  written_off: { variant: 'neutral', label: 'Written off' },
  superseded: { variant: 'neutral', label: 'Superseded' },
};

// A refund attempt's own lifecycle — the provider-side event a refund
// obligation is reconciled against. 'unknown'/'sending'/'pending' all mean
// "we don't yet know the outcome", hence the same warning tone; 'failed' is
// the one truly bad terminal state.
export const FINANCE_ATTEMPT_STATUS_TONE: Record<FinanceRefundAttemptStatus, Tone> = {
  sending: { variant: 'warning', label: 'Sending' },
  unknown: { variant: 'warning', label: 'Unknown' },
  pending: { variant: 'warning', label: 'Pending' },
  completed: { variant: 'success', label: 'Completed' },
  failed: { variant: 'danger', label: 'Failed' },
  abandoned: { variant: 'neutral', label: 'Abandoned' },
};
